/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2017, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../lib/@waca/core-client/js/core-client/utils/ClassFactory',
	'../../lib/@waca/core-client/js/core-client/i18n/Formatter',
	'./FilterDockItemView',
	'../../widgets/livewidget/nls/StringResources',
	'jquery',
	'underscore',
	'../FilterMetadataHelper',
	'../finder/FilterModuleFinder',
	'../../util/DashboardFormatter'
],
function(Class, AMD, Formatter, FilterDockItemView, resources, $, _,
	FilterMetadataHelper, FilterModuleFinder, DashboardFormatter) {

	var FilterDockItemController = Class.extend({

		_AMD: AMD,

		/**
			 * Constructor
			 *
			 * @param {object} params - arguments
			 *
			 * @constructs FilterDockItemController
			 */
		init: function(params) {
			FilterDockItemController.inherited('init', this, arguments);

			this.dashboardApi = params.dashboardApi;
			this.featureChecker = params.featureChecker;
			this.pageContextAPI = params.pageContextAPI;
			this.scope = params.scope;
			this.item = params.item;
			this.filterContent = params.filterContent;
			this.dataFilters = params.dataFilters;
			this.filtersUI = params.filtersUI;
			this.cascadingFilters = params.cascadingFilters;
			this.onDelete = params.onDelete;
			this.dockKey = params.dockKey;
			this.getFilterItem = params.getFilterItem;
			this.ajaxSvc = params.ajaxSvc;
			this.parentLayoutId = params.parentLayoutId;
			this.singleDockMode = params.singleDockMode;
			this.excludeAddToCanvas = params.excludeAddToCanvas;
			this.id = _.uniqueId('FilterDockItemController_');
			this.dataSources = params.dataSources;
			this.cascadeSets = params.cascadeSets;
			this.cascadeSetsController = params.cascadeSetsController;

			this.transaction = this.dashboardApi.getFeature('Transaction');

			//listen for browser window resize, adjust mountNode as needed
			this.windowResizeHandler = this._onWindowResize.bind(this);
			$(window).on('resize', this.windowResizeHandler);

			this.registerEvents();

			let filterOptions = {
				menuActionHandler: this.menuActionHandler.bind(this),
				getFilterController: this.getFilterController.bind(this),
				isAuthoringMode: this.dashboardApi.getMode() === 'authoring',
				iconsFeature: this.dashboardApi.getFeature('Icons')
			};
			this.ready = this._whenMetadataReady()
				.then(() => {
					if (this.item) { // Ensure item exists. Remove might have been invoked while the async function runs
						_.extend(filterOptions, {
							onOpenDropdownFilterView: this.openDropdownFilterView.bind(this),
							isItemAHierarchy: this.isHierarchy()
						});
					}
				}).catch(e => {
					this.excludeAddToCanvas = true;
					_.extend(filterOptions, {
						onOpenDropdownFilterView: this.openDropdownErrorFilterView.bind(this, e),
						missingData: true
					});
				}).finally(() => {
					this.view = new FilterDockItemView(_.extend(filterOptions, {
						viewModel: this._getViewModel(),
						getViewModel: this._getViewModel.bind(this)
					}));
					return this.registerSourceUsage();
				});

		},

		_onWindowResize: function() {
			if (this.view && this.filterInstance && this.filterInstance.isOpen()) {
				this.view.adjustMountNodeHeight(this.filterInstance.MIN_HEIGHT, this.filterInstance.HEIGHT);
			}
		},

		_getItemMetadataLabel: function () {
			let metadata;
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				metadata = this._getContentMetadataColumn();

			} else {
				const dataSources = this.dashboardApi.getFeature('DataSources');
				const sourceId = this.getDataSourceId();
				const columnId = this.getColumnId();
				metadata = dataSources.getDataSource(sourceId).getMetadataColumn(columnId);
			}
			return metadata && metadata.getLabel();
		},

		_getDataFilter() {
			const columnIds = this._getContentDataItems().map(dataItem => dataItem.getColumnId());
			const dataFilters = this.dataFilters.getFilterList({
				contentType: 'self',
				columnIds
			});
			return dataFilters && dataFilters.length && dataFilters[0];
		},

		_getContentDataItems() {
			const slots = this.filterContent.getFeature('Visualization').getSlots();
			return slots.getDataItemList();
		},

		_getContentDataItemsLabel() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentDataItems().map(dataItem => dataItem.getLabel()).join(', ');
			}
		},

		_getContentMetadataColumn() {
			const slots = this.filterContent.getFeature('Visualization').getSlots();
			const dataItems = slots.getDataItemList();
			return dataItems && dataItems.length && dataItems[0].getMetadataColumn();
		},

		getDataSourceId() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataSource = this.filterContent.getFeature('Visualization').getDataSource();
				return dataSource && dataSource.getId();
			}
			return this.item.getSourceId();
		},

		getColumnId() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const columnIds = dataFilter.getColumnIdList();
				return columnIds && columnIds.length && columnIds[0];
			}
			return this.item.getItemId();
		},

		getScope() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const container = this.filterContent.getContainer();
				return !container.getContainer() ? 'global' : container.getId();
			}
			return this.item.getScope();
		},

		getDataType() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().getDataType();
			}
			return this.item.getDataType();
		},

		getType() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().getType();
			}
			return this.item.getUsage();
		},

		getName() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().getLabel();
			}
			return this.item.getItemName();
		},

		getValueCount() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const valueList = dataFilter.getValueList();
				if (this.isRange()) {
					return valueList[0].from === 0 && valueList[0].to === 0 ? 0 : valueList.length;
				}
				return valueList ? valueList.length : 0;
			}
			return this.item.getValueCount();
		},

		getValueCaptions() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				return dataFilter.getValueList().map(value => {
					const tupleLabels = value.map(v => v.label);
					return tupleLabels.join(', ');
				});
			}
			return this.item.getValueCaptions();
		},

		isModeledFilter() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().isFilter();
			}
			return this.item.getIsModeledFilter();
		},

		isHierarchy() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().isHierarchy();
			}
			return this.item.isHierarchy();
		},

		isNamedSet() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				return this._getContentMetadataColumn().isNamedSet();
			}
			return this.item.getIsNamedSet();
		},

		isValueDataItem() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const values = dataFilter.getValueList();
				return this.isNamedSet() && !(values && values.length);
			}
			return this.item.isValueDataItem();
		},

		isExclude() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				return dataFilter.isExclude();
			}
			return this.item.getExclude();
		},

		isColumnUnavailable() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataItems = this._getContentDataItems();
				return dataItems.some(dataItem => dataItem.isColumnUnavailable());
			}
			return this.item.getIsColumnUnavailable();
		},

		isRange() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const values = dataFilter.getValueList();
				return (values && values.length) ? values[0].hasOwnProperty('from') || values[0].hasOwnProperty('to') : false;
			}
			return this.item.isRange();
		},

		getFrom() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const values = dataFilter.getValueList();
				return (values && values.length) ? values[0].from : null;
			}
			return this.item.getFrom();
		},

		getTo() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const values = dataFilter.getValueList();
				return (values && values.length) ? values[0].to : null;
			}
			return this.item.getTo();
		},

		getInvert() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const dataFilter = this._getDataFilter();
				const values = dataFilter.getValueList();
				return (values && values.length) ? !!values[0].invert : false;
			}
			return this.item.getInvert();
		},

		getError() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const state = this.filterContent.getFeature('state');
				return state.getError();
			}
			return this.item.getError();
		},

		getMaximumFractionDigits() {
			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const metadataColumn = this._getContentMetadataColumn();
				const format = metadataColumn && metadataColumn.getFormat();
				return format && format.maximumFractionDigits;
			}
			const info = this.item.getMetadataInfo();
			return info && info.maximumFractionDigits;
		},

		_getViewModel: function() {
			return {
				name: this._getContentDataItemsLabel() || this._getItemMetadataLabel() || this.item.getItemName(),
				dockKey: this.dockKey,
				contentId: this.filterContent && this.filterContent.getId(),
				text: this._getFilterText() || this.generateAllMembersString(),
				isRange: this.isRange(),
				isNamedSet: this.isNamedSet(),
				selectedCount: this._getSelectCount(),
				menuItems: this._getMenuItems(),
				menuIcon: 'wa-glass_menu',
				error: this.getError(),
				overflowMenuAriaLabel: resources.get('filterOverflowMenu'),
				isModeledFilter: this.isModeledFilter()
			};
		},

		/**
			 * Register events
			 */
		registerEvents: function() {
			this.dashboardApi.on('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboardApi.on('filterDock:showItemMenu', this.showItemMenu, this);
			this.dashboardApi.on('filterDock:hideItemMenu', this.hideItemMenu, this);
		},

		/**
			 * Unregister events
			 */
		unregisterEvents: function() {
			this.dashboardApi.off('pagecontext:filterContextUpdated', this.onFilterContextUpdated, this);
			this.dashboardApi.off('filterDock:hideItemMenu', this.showItemMenu, this);
			this.dashboardApi.off('filterDock:hideItemMenu', this.hideItemMenu, this);
		},

		registerSourceUsage: function() {
			const dataSourceSvc =  this.dashboardApi.getFeature('dataSources.deprecated');
			this.sourceCollection = dataSourceSvc.getSourcesCollection();
			this.sourceCollection.registerUsage(this.id, this.getDataSourceId());
		},

		unregisterSourceUsage: function() {
			if (this.sourceCollection) {
				this.sourceCollection.deregisterUsage(this.id);
			}
		},

		/**
			 *
			 * @return {Array} Array of actions for the filter item menu
			 */
		_getMenuItems: function() {
			var actions = [];
			// do not add canvas action to menu when excludeAddToCanvas flag is set to true
			if (!this.excludeAddToCanvas && !this.isModeledFilter() && (this.singleDockMode || this.getScope() !== 'global')) {
				actions.push({
					'label': resources.get('filterItemCanvasMove'),
					'action': 'filter-menu-add-to-canvas-action'
				});
			}

			if(this._isCascadingFiltersEnabled()){
				const menuItem = {
					'label': resources.get('addToCascadeSet'),
					'action': 'add-to-cascadeset'
				};

				if(this.cascadeSets.findCascadeSet(this.getColumnId(), this.getDataSourceId())){
					menuItem.label =  resources.get('removeFromCascadeSet');
					menuItem.action = 'remove-from-cascadeset';
				}
				actions.push(menuItem);
			}

			actions.push({
				'label': resources.get('filterItemDelete'),
				'action': 'filter-menu-delete-action'
			});

			return actions;
		},

		_isCascadingFiltersEnabled(){
			return this.cascadingFilters.isCascadingFiltersEnabled() && !!this.cascadeSets;
		},

		/**
			 * @return {String} Comma separated value of selections on the filter item
			 */
		generateSelectedValueString: function() {
			var str;

			if (this.isRange()) {
				var from = this.getFrom();
				var to = this.getTo();
				if (from === '' && to === '' || from === 0 && to === 0) {
					str = '';
				} else {
					str = this._getLabel(from, to);
				}
			} else if (this.isValueDataItem()) {
				// When an item is based the entire named set, there is no filter value.
				str = resources.get('filter_allMembersOfSet');
			} else {
				str = this._generateMemberString();
			}

			return str;
		},

		generateAllMembersString: function() {
			return this.isNamedSet() ? resources.get('filter_allMembersOfSet') : '';
		},

		/**
			 * Generates and return docker filter item labelFromTo
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {string} the label string
			 */
		_getLabel: function(from, to) {
			var str;
			var dataType = this.getDataType();
			if (FilterDockItemController.isDateDataType(dataType)) {
				var result = this._formatDateTime(dataType, from, to);
				str = this._generateDateTimeLabel(result, from, to, this.getInvert());
			}

			//Fallback to current default format if not date dataType or failed to localized format date dataType
			if (!str) {
				var spec = {
					type: dataType
				};
				const maximumFractionDigits = this.getMaximumFractionDigits();
				if (maximumFractionDigits !== undefined) {
					spec.maximumFractionDigits = maximumFractionDigits;
				}
				from = Formatter.format(from, spec);
				to = Formatter.format(to, spec);
				str = resources.get(this.getInvert() ? 'filterInvertRange' : 'filterRange', {
					from: from,
					to: to
				});
			}
			return str.length ? str : '';
		},

		/**
			 * Generates and return the label string for date time data type
			 *
			 * @param {object} context - the context use to generate the label
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 * @param {boolean} isInvert - invert boolean flag
			 *
			 * @return {string} the label string
			 */
		_generateDateTimeLabel: function(context, from, to, isInvert) {
			var label;
			if (context) {
				from = context.from || from;
				to = context.to || to;
				if (this._dateIsAfter(from, to)) {
					label = resources.get(isInvert ? 'filter_date_after_except' : 'filter_date_after', {
						from: from
					});
				} else if (this._dateIsBefore(from, to)) {
					label = resources.get(isInvert ? 'filter_date_before_except' : 'filter_date_before', {
						to: to
					});
				} else if (this._dateIsBetween(from, to)) {
					label = resources.get(isInvert ? 'filter_date_between_except' : 'filter_date_between', {
						from: from,
						to: to
					});
				}
			}
			return label;
		},

		/**
			 * Returns true for 'Date is after' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is after' else return false
			 */
		_dateIsAfter: function(from, to) {
			return (from && !!from.length) && (!to || !to.length) ? true : false;
		},

		/**
			 * Returns true for 'Date is before' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is before' else return false
			 */
		_dateIsBefore: function(from, to) {
			return (to && !!to.length) && (!from || !from.length) ? true : false;
		},

		/**
			 * Returns true for 'Date is between' else return false
			 *
			 * @param {string} from - the from string
			 * @param {string} to - the to string
			 *
			 * @return {boolean} return true for 'Date is between' else return false
			 */
		_dateIsBetween: function(from, to) {
			return (from && !!from.length) && (to && !!to.length) ? true : false;
		},

		_formatDateTime: function(dataType, from, to /*, tupleSet*/ ) {
			var result;
			if (!!from.length || !!to.length) {
				result = {
					from: '',
					to: ''
				};
				// var Utilities = I18NUtils.getI18NUtils();
				// result.from = from; /*this._localizedDateTime(from, dataType, Utilities);*/
				// result.to = to; /*this._localizedDateTime(to, dataType, Utilities);*/

				if (from.length) {
					result.from = Formatter.format(from, {
						type: dataType
					});
				}

				if (to.length) {
					result.to = Formatter.format(to, {
						type: dataType
					});
				}
			}
			return result;
		},

		_generateMemberString: function() {
			let str;
			const selectedContextItems = [];
			let captions = this.getValueCaptions();
			_.each(captions, function(caption) {
				const isNotNullValue = caption || caption === 0;
				selectedContextItems.push(isNotNullValue ? caption : DashboardFormatter.format(null));
			});

			str = selectedContextItems.join(resources.get('filterValueSeparator') + ' ');
			if (this.isExclude()) {
				if (this.getValueCaptions().length === 0) {
					str = resources.get('filterAll');
				} else {
					str = resources.get('filterExcept', {
						values: str
					});
				}
			}

			return str;
		},

		/**
			 * Handle the filter item menu click action.
			 *
			 * @param {Jquery event} Event containing menu action to handle
			 */
		menuActionHandler: function(event) {
			//TODO Externalize this handler
			if ($(event.currentTarget).hasClass('disabled')) {
				return;
			}

			var action = event.currentTarget.dataset.action;
			if (action === 'filter-menu-delete-action') {
				this._deleteSelf();
			} else if (action === 'filter-menu-add-to-canvas-action') {
				this._addToCanvas();
			}else if(action === 'add-to-cascadeset'){
				this._addToCascadeSet();
			}else if(action === 'remove-from-cascadeset'){
				this._removeFromCascadeSet(this.getColumnId(), this.getDataSourceId());
			}
		},

		/*
			 * The metadata may not be available dependending on when the filter item was
			 * created. Make sure to load metadata if it's not available from pageContextEntry.
			 */
		_whenMetadataReady: function() {
			const dataSources = this.dashboardApi.getFeature('dataSources.deprecated');
			this.dataSources = dataSources;

			var metadata = this.item.getMetadataInfo();
			if (metadata) {
				return new Promise((resolve, reject) => {
					if (this.isColumnUnavailable()) {
						reject(new Error(resources.get('errMsgFilterItemMissingTitle', {
							itemName: this.getColumnId()
						})));
					}
					resolve(null);
				});
			}

			return FilterMetadataHelper.getColumnMetadataInfo(dataSources, this.getDataSourceId(), this.getColumnId())
				.then((metadata) => {
					if (!_.isEmpty(metadata)) {
						this.item.updateMetadata(metadata);
					} else {
						throw new Error(resources.get('errMsgFilterItemMissingTitle', {
							itemName: this.getColumnId()
						}));
					}
				});


		},

		_addToCanvas: function() {
			const transactionToken = this.transaction.startTransaction();
			const containerId = this._getAddToCanvasParentId();
			const canvas = this.dashboardApi.getFeature('Canvas');
			const content = {
				spec: {
					properties: this._getItemContextProperties(),
					type: 'filter'
				},
				containerId: containerId
			};
			const templateDropZones = canvas.getContent(containerId).findContent({ type: 'templateDropZone' });
			if (templateDropZones.length === 0){
				content.layout = {
					height: (['string', 'date', 'datetime', 'time', 'timestamp'].indexOf(this.getDataType().toLowerCase()) !== -1) ? '532px' : '382px',
					width: '350px'
				};
			}
			canvas.addContent(
				content, transactionToken
			);
		},

		//Add To Cascade Set Menu handler....
		_addToCascadeSet() {
			let datasourceCascadeSets = this.cascadeSets.getCascadeSetList().filter((cascadeSet) => cascadeSet.getDataSourceId() === this.getDataSourceId());
			//Dashboad UI only supports 1 cascade per datasource
			if(datasourceCascadeSets.length > 1){
				throw new Error(`Unexpected number of cascade sets for datasource ${this.getDataSourceId()}`);
			}else{
				const transactionToken = this.transaction.startTransaction();
				this.cascadeSets && this.cascadeSets.addToCascadeSet(this.getColumnId(), this.getDataSourceId(), /*id*/null, /*atEnd*/-1, transactionToken);
				this.transaction.endTransaction(transactionToken);
			}
		},

		//Remove Cascade Set Menu handler....include the transactionToken if defined.
		_removeFromCascadeSet(columnId, dataSourceId, transactionToken){
			if (this._isCascadingFiltersEnabled()) {
				const isLocalTransaction = !transactionToken;
				transactionToken = transactionToken || this.transaction.startTransaction();
				this.cascadeSets.removeFromCascadeSet(columnId, dataSourceId, transactionToken);
				isLocalTransaction && this.transaction.endTransaction(transactionToken);
			}
		},

		//Called when an item is deleted to update cascadeSets (either remove from a set or adjust sets positioned on the deleted item)
		_updateCascadeSetsOnDeleteItem(columnId, dataSourceId, transactionToken){
			if (this.cascadeSets && this._isCascadingFiltersEnabled()) {
				//Set the "isDeletingItem" flag to keep from re-rendering the view / invalidating this item prior to updating page context.
				this.cascadeSetsController.setIsDeletingFilterItem(true);
				this.cascadeSets.deleteItem(columnId, dataSourceId, transactionToken);
				this.cascadeSetsController.setIsDeletingFilterItem(false);
			}
		},

		_getAddToCanvasParentId: function() {
			var parentId = this.getScope();

			if (this.singleDockMode) {
				parentId = this.parentLayoutId;
			}
			return parentId;
		},

		_deleteItemFromPageContext(transactionToken) {
			const options = {
				payloadData: {
					undoRedoTransactionId: transactionToken.transactionId,
					transactionToken: transactionToken
				}
			};

			this.pageContextAPI.updateFilterContext({
				item: this.item,
				scope: this.scope,
				origin: 'filter',
				eventSourceId: this.filterInstance ? this.filterInstance.id : null
			}, {
				command: 'delete'
			}, options);

			if (this.filterContent) {
				this.filtersUI.removeFilterContent(this.filterContent.getId(), transactionToken);
				delete this.filterContent;
			}
		},

		_deleteSelf: function() {
			this.dashboardApi.triggerDashboardEvent('filterItem:filterItemBlur');
			const transactionToken = this.transaction.startTransaction();
			const columnId = this.getColumnId();
			const dataSourceId = this.getDataSourceId();
			this._updateCascadeSetsOnDeleteItem(columnId, dataSourceId, transactionToken);
			this._deleteItemFromPageContext(transactionToken);
			this.transaction.endTransaction(transactionToken);
			this.onDelete(this);
		},

		onFilterContextUpdated: function(payload) {
			payload = payload || {};
			if(payload.ignorePageContextChanged && payload.ignorePageContextChanged()) {
				return;
			}

			if (this.view) {
				this.view.updateMenuState(this._getViewModel());
			}
		},

		_getSelectCount: function() {
			// When invert is on, ideally we want to show distCount-valueCount.
			// However distCount is from MetadataApi, and there is a performance impact to expose it.
			// For now we are showing '-' as Not Applicable.
			return this.isExclude() ? '-' : this.getValueCount();
		},

		openDropdownErrorFilterView: function(error) {
			this.view.initializeMountNodeForDropDownFilterView();
			this.view.processError(error);
		},

		/**
		 * Opens the filter item view (such as the member picker)
		 */
		openDropdownFilterView: function () {
			this.view.initializeMountNodeForDropDownFilterView();
			let isFilterViewLoaded = this.view.isFilterViewLoaded();

			if (this.featureChecker.checkValue('dashboard', 'DataFilters', 'enabled')) {
				const visDefinition = this.filterContent.getFeature('Visualization').getDefinition();
				this.view.renderInFilterItemView({ viewClass: visDefinition.getProperty('viewClass') });
				return this.filterContent.render(!isFilterViewLoaded ? this.view.getWidgetMountNode()[0] : null);
			} else {
				return this.getFilterController().then(() => {
					this.view.adjustMountNodeHeight(this.filterInstance.MIN_HEIGHT, this.filterInstance.HEIGHT);
					return !isFilterViewLoaded ? this.filterInstance.render() : this.filterInstance.onOpen();
				}).catch(this.view.processError.bind((this.view)));
			}
		},

		/**
			 * Set the filter controller instance and return it
			 * @returns filter controller instance.
			 */
		getFilterController: function() {
			if (this.filterInstance) {
				return this.filterInstance.ready.then(function() {
					return Promise.resolve(this.filterInstance);
				}.bind(this))
					.catch(function(err) {
						return Promise.reject(err);
					});
			}
			const isRange = this.isRange();
			const finderOpts = _.extend({ origin: 'filterDock', isRange }, this.item.getMetadataInfo());

			return FilterModuleFinder.getModule(finderOpts)
				.then(function(module) {
					return this._initializeFilterController(module);
				}.bind(this));
		},

		_initializeFilterController: function(module) {
			return this._AMD.loadModule(module).then(function(module) {
				var FilterController = module;
				var params = this._getFilterControllerParams();

				// we need to make 100% sure that when we set this.filterInstance that the instance is ready to use since we will never get back here.
				let filterInstance = new FilterController(params.options, params.extraOptions);
				filterInstance.viewConfig.showHeader = false;
				return filterInstance.initialize().then(function() {
					this.filterInstance = filterInstance;
					return Promise.resolve(this.filterInstance);
				}.bind(this));
			}.bind(this)).catch(function (err) {
				if (this.filterInstance) {
					this.filterInstance.remove();
					this.filterInstance = null;
				}
				return this.view.processError(err);
			}.bind(this));
		},

		_getFilterControllerParams: function() {
			var params = {};

			params.options = {
				dashboardApi: this.dashboardApi,
				pageContextAPI: this.pageContextAPI,
				containerPageId: this.scope,
				initialConfigJSON: {
					properties: this._getItemContextProperties()
				},
				ajaxSvc: this.ajaxSvc,
				hasSearchIcon: true
			};

			params.extraOptions = {
				commonView: this.view
			};

			return params;

		},

		_getItemContextProperties: function(){
			return {
				itemName: this.item.getItemName(),
				itemId: this.getColumnId(),
				sourceId: this.getDataSourceId(),
				dataType: this.getDataType(),
				isNamedSet: this.isNamedSet(),
				usage: this.getType(),
				isHierarchy: this.isHierarchy(),
				scope: this.getScope()
			};
		},

		/**
			 * update the view. This function is invoked when there is
			 * a change in the page context item
			 */
		update: function() {
			if (this.view) {
				const _viewModel = {
					name: this._getItemMetadataLabel() || this.item.getItemName(),
					text: this._getFilterText(),
					selectedCount: this._getSelectCount(),
					isModeledFilter: this.isModeledFilter(),
					error: this.getError()
				};
				this.view.update(_viewModel);
			}
		},

		_getFilterText(){
			return this.isModeledFilter() ? resources.get('modelFilter'): this.generateSelectedValueString();
		},

		getItemDOMnode: function() {
			return this.view.$el;
		},

		remove: function() {
			this.unregisterEvents();
			this.unregisterSourceUsage();

			if (this.filterInstance) {
				this.filterInstance.remove();
				this.filterInstance = null;
			}

			if (this.windowResizeHandler) {
				$(window).off('resize', this.windowResizeHandler);
			}

			if (this.view) {
				this.view.remove();
				this.view = null;
			}

			this.pageContextAPI = null;
			this.scope = null;
			this.item = null;
		},

		showItemMenu: function() {
			if (this.view && this.view.showItemMenu) {
				this.view.showItemMenu();
			}
		},

		hideItemMenu: function() {
			if (this.view && this.view.hideItemMenu) {
				this.view.hideItemMenu();
			}
		}

	});

	//Static method
	/**
	 *  Generates a key based on item id and asset id of a page context item
	 * @return key in the format 'itemId/assetId'
	 *
	 */
	FilterDockItemController.genFilterItemDockKey = function(item) {
		return item.getKey();
	};

	/**
		 * Returns a boolean indicating whether the dataType is a date/time data type or not
		 *
		 * @return {boolean} true if dataType is a date/time data type else return false
		 */
	FilterDockItemController.isDateDataType = function(type) {
		return (type === 'date' || type === 'time' || type === 'datetime' || type === 'timestamp');
	};

	return FilterDockItemController;
});
